import { Checkbox } from "@mui/material"
import type { ColumnDef, RowSelectionState } from "@tanstack/react-table"
import type { FC } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { CompetitorProduct } from "@/graphql/codegen/graphql"
import { useCompetitorProductsColumns } from "@/screens/ManageProducts/components/CompetitorProducts/hooks/useCompetitorProductsColumns"

interface CompetitorProductsTableProps {
  products: CompetitorProduct[]
  loading: boolean
  selected: Record<string, boolean>
  onSelectionChange: (selected: Record<string, boolean>) => void
  associatedIds?: string[]
}

export const CompetitorProductsTable: FC<CompetitorProductsTableProps> = ({
  products,
  loading,
  selected,
  onSelectionChange,
  associatedIds = [],
}) => {
  const handleRowSelectionChange = (
    updaterOrValue: ((old: RowSelectionState) => RowSelectionState) | RowSelectionState
  ) => {
    const newSelection = typeof updaterOrValue === "function" ? updaterOrValue(selected) : updaterOrValue

    onSelectionChange(newSelection)
  }

  // Get base columns from the reusable hook
  const baseColumns = useCompetitorProductsColumns()

  // Filter out the actions column and technicalFile column
  const dataColumns = baseColumns.filter((col) => {
    const key = "accessorKey" in col ? col.accessorKey : null
    return key !== "actions" && key !== "technicalFile"
  })

  const columns: ColumnDef<CompetitorProduct>[] = [
    {
      id: "select",
      header: "",
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 50,
    },
    ...dataColumns,
  ]

  return (
    <AerosBaseTable
      features={{
        tanstackOptions: {
          data: products,
          columns,
          state: {
            rowSelection: selected,
            isLoading: loading,
          },
          onRowSelectionChange: handleRowSelectionChange,
          getRowId: (row) => row.competitorProductId,
          enableRowSelection: (row) => !associatedIds.includes(row.original.competitorProductId),
        },
      }}
      slotProps={{
        bodyRow: {
          className: "cursor-pointer hover:bg-primary-100",
        },
      }}
      noDataMessage='No products found'
    />
  )
}
